import React, { useState, useEffect } from 'react';
import { Shield, TrendingUp, AlertTriangle, CheckCircle, Clock, Award, Target, Zap } from 'lucide-react';
import { secureApi } from '../utils/auth';

const ComplianceDashboard = ({ userId }) => {
  const [complianceScore, setComplianceScore] = useState(0);
  const [trend, setTrend] = useState([]);
  const [issues, setIssues] = useState([]);
  const [streak, setStreak] = useState(0);
  const [nextAudit, setNextAudit] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadComplianceData();
  }, [userId]);

  const loadComplianceData = async () => {
    try {
      // In production, these would be real API calls
      // For now, calculate based on user's documents
      const response = await secureApi.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/documents`
      );
      
      const docs = response.documents || [];
      const totalDocs = docs.length;
      const compliantDocs = docs.filter(d => d.status === 'completed').length;
      
      const score = totalDocs > 0 ? Math.round((compliantDocs / totalDocs) * 100) : 0;
      setComplianceScore(score);
      
      // Mock trend data (last 30 days)
      const mockTrend = Array.from({ length: 30 }, (_, i) => ({
        day: i + 1,
        score: Math.max(0, Math.min(100, score + (Math.random() - 0.5) * 20))
      }));
      setTrend(mockTrend);
      
      // Mock issues
      const mockIssues = docs
        .filter(d => d.status === 'failed' || d.status === 'pending')
        .slice(0, 5)
        .map(d => ({
          id: d.id,
          title: `Review ${d.filename}`,
          severity: 'medium',
          daysOpen: Math.floor(Math.random() * 7) + 1
        }));
      setIssues(mockIssues);
      
      // Mock streak
      setStreak(score >= 90 ? 15 : 0);
      
      // Mock next audit
      setNextAudit({
        date: new Date(Date.now() + 45 * 24 * 60 * 60 * 1000),
        type: 'OSHA',
        ready: score >= 90
      });
      
      setLoading(false);
    } catch (error) {
      console.error('Failed to load compliance data:', error);
      setLoading(false);
    }
  };

  const getScoreColor = (score) => {
    if (score >= 90) return 'text-green-600';
    if (score >= 70) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getScoreBgColor = (score) => {
    if (score >= 90) return 'bg-green-50 border-green-200';
    if (score >= 70) return 'bg-yellow-50 border-yellow-200';
    return 'bg-red-50 border-red-200';
  };

  const getScoreStatus = (score) => {
    if (score >= 90) return 'COMPLIANT';
    if (score >= 70) return 'NEEDS ATTENTION';
    return 'AT RISK';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Main Compliance Score Circle */}
      <div className={`rounded-lg border-2 p-8 ${getScoreBgColor(complianceScore)}`}>
        <div className="flex items-center justify-between">
          <div className="flex-1">
            <div className="flex items-center gap-3 mb-4">
              <Shield className={`h-12 w-12 ${getScoreColor(complianceScore)}`} />
              <div>
                <h2 className="text-2xl font-bold text-gray-900">Compliance Status</h2>
                <p className="text-gray-600">Real-time compliance monitoring</p>
              </div>
            </div>
            
            <div className="flex items-baseline gap-2">
              <span className={`text-6xl font-bold ${getScoreColor(complianceScore)}`}>
                {complianceScore}%
              </span>
              <span className={`text-2xl font-semibold ${getScoreColor(complianceScore)}`}>
                {getScoreStatus(complianceScore)}
              </span>
            </div>
            
            {complianceScore >= 90 && (
              <div className="mt-4 flex items-center gap-2 text-green-700">
                <CheckCircle className="h-5 w-5" />
                <span className="font-medium">Ready for audit</span>
              </div>
            )}
          </div>
          
          {/* Circular Progress */}
          <div className="relative w-48 h-48">
            <svg className="transform -rotate-90 w-48 h-48">
              <circle
                cx="96"
                cy="96"
                r="88"
                stroke="currentColor"
                strokeWidth="12"
                fill="transparent"
                className="text-gray-200"
              />
              <circle
                cx="96"
                cy="96"
                r="88"
                stroke="currentColor"
                strokeWidth="12"
                fill="transparent"
                strokeDasharray={`${2 * Math.PI * 88}`}
                strokeDashoffset={`${2 * Math.PI * 88 * (1 - complianceScore / 100)}`}
                className={getScoreColor(complianceScore)}
                strokeLinecap="round"
              />
            </svg>
            <div className="absolute inset-0 flex items-center justify-center flex-col">
              <span className={`text-4xl font-bold ${getScoreColor(complianceScore)}`}>
                {complianceScore}
              </span>
              <span className="text-sm text-gray-600">Score</span>
            </div>
          </div>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        {/* Streak */}
        <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Compliant Streak</p>
              <p className="text-3xl font-bold text-blue-600 mt-1">{streak} days</p>
            </div>
            <Award className="h-10 w-10 text-blue-500" />
          </div>
          {streak > 0 && (
            <div className="mt-3 flex items-center gap-1 text-sm text-green-600">
              <Zap className="h-4 w-4" />
              <span>Keep it up!</span>
            </div>
          )}
        </div>

        {/* Next Audit */}
        <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Next Audit</p>
              <p className="text-xl font-bold text-gray-900 mt-1">
                {nextAudit ? Math.ceil((nextAudit.date - new Date()) / (1000 * 60 * 60 * 24)) : '-'} days
              </p>
              <p className="text-xs text-gray-500 mt-1">{nextAudit?.type}</p>
            </div>
            <Clock className="h-10 w-10 text-gray-400" />
          </div>
          {nextAudit?.ready && (
            <div className="mt-3 text-sm text-green-600 font-medium">
              ✓ Ready
            </div>
          )}
        </div>

        {/* Open Issues */}
        <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Open Issues</p>
              <p className="text-3xl font-bold text-orange-600 mt-1">{issues.length}</p>
            </div>
            <AlertTriangle className="h-10 w-10 text-orange-500" />
          </div>
          {issues.length === 0 && (
            <div className="mt-3 text-sm text-green-600 font-medium">
              ✓ All clear
            </div>
          )}
        </div>

        {/* Trend */}
        <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">30-Day Trend</p>
              <p className="text-3xl font-bold text-green-600 mt-1">↑ 12%</p>
            </div>
            <TrendingUp className="h-10 w-10 text-green-500" />
          </div>
          <div className="mt-3 text-sm text-gray-600">
            Improving
          </div>
        </div>
      </div>

      {/* Trend Graph */}
      <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-200">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Compliance Trend (30 Days)</h3>
        <div className="h-48 flex items-end justify-between gap-1">
          {trend.slice(-30).map((point, index) => (
            <div
              key={index}
              className="flex-1 bg-blue-500 rounded-t hover:bg-blue-600 transition-colors"
              style={{ height: `${point.score}%` }}
              title={`Day ${point.day}: ${Math.round(point.score)}%`}
            />
          ))}
        </div>
        <div className="flex justify-between text-xs text-gray-500 mt-2">
          <span>30 days ago</span>
          <span>Today</span>
        </div>
      </div>

      {/* Open Issues List */}
      {issues.length > 0 && (
        <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Action Items</h3>
          <div className="space-y-3">
            {issues.map((issue, index) => (
              <div key={index} className="flex items-center justify-between p-3 bg-orange-50 border border-orange-200 rounded-lg">
                <div className="flex items-center gap-3">
                  <AlertTriangle className="h-5 w-5 text-orange-600" />
                  <div>
                    <p className="font-medium text-gray-900">{issue.title}</p>
                    <p className="text-sm text-gray-600">Open for {issue.daysOpen} days</p>
                  </div>
                </div>
                <button className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm">
                  Review
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Department Heat Map */}
      <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-200">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Department Compliance</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
            { name: 'Safety', score: 95, color: 'green' },
            { name: 'HR', score: 88, color: 'yellow' },
            { name: 'Operations', score: 92, color: 'green' },
            { name: 'Finance', score: 97, color: 'green' }
          ].map((dept, index) => (
            <div key={index} className={`p-4 rounded-lg border-2 ${
              dept.color === 'green' ? 'bg-green-50 border-green-200' : 'bg-yellow-50 border-yellow-200'
            }`}>
              <p className="text-sm text-gray-600 mb-1">{dept.name}</p>
              <p className={`text-2xl font-bold ${
                dept.color === 'green' ? 'text-green-600' : 'text-yellow-600'
              }`}>{dept.score}%</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default ComplianceDashboard;
